import { useEffect } from "react"
import { useRouter } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"

import { useWebApp } from "@/features/webapp"
import { ReportIds, useUserReports } from "@/features/today"

import { isSoulmateReport } from "../types"
import { SoulmateReportView } from "./soulmate-report-view"

/**
 * Soulmate Sketch tab (`/webapp/soulmate`). Reads the report straight from the
 * shared reports list (`GET /v1/reports/me`), which now carries the soulmate
 * content inline — so no separate fetch. Renders the full native report once
 * `ready`, otherwise a generating state while the backend composes it, polling
 * the list until it flips. Full-bleed, so the shell header is hidden while mounted.
 */
export function SoulmatePage() {
  const router = useRouter()
  const { setHeaderHidden } = useWebApp()
  const { byType, loading, refetch } = useUserReports()

  const report = byType(ReportIds.Soulmate)
  const status = report?.status
  const content = report?.content && isSoulmateReport(report.content) ? report.content : null

  // The report has its own hero navbar — suppress the shell header while mounted.
  useEffect(() => {
    setHeaderHidden(true)
    return () => setHeaderHidden(false)
  }, [setHeaderHidden])

  // While the backend is still generating, re-pull the list until it's ready.
  useEffect(() => {
    if (status !== "processing") return
    const timer = setInterval(() => void refetch(), 5000)
    return () => clearInterval(timer)
  }, [status, refetch])

  if (status === "ready" && content) {
    return (
      <div className="-mx-4 bg-dark-base">
        <SoulmateReportView content={content} />
      </div>
    )
  }

  return (
    <div className="-mx-4">
      <SoulmateGenerating loading={loading && !report} onBack={() => router.history.back()} />
    </div>
  )
}

/** Placeholder shown while the backend is still generating the report. */
function SoulmateGenerating({ loading, onBack }: { loading: boolean; onBack: () => void }) {
  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-dark-base px-8 text-center">
      <div className="absolute inset-x-0 top-0 flex h-14 items-center px-4">
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          className="flex size-8 items-center justify-center text-white [&_svg]:size-6"
        >
          <ArrowLeft />
        </button>
      </div>

      <span className="size-12 animate-spin rounded-full border-[3px] border-purple-light/20 border-t-purple-light" />
      <h1 className="mt-6 font-display text-2xl font-medium text-purple-light">
        Composing your Soulmate Sketch
      </h1>
      <p className="mt-2 max-w-[300px] font-body text-base text-purple-light/70">
        {loading
          ? "Reading your chart…"
          : "Your reading is being drawn from the stars. This can take a moment — check back shortly."}
      </p>
    </div>
  )
}
